import React, {useState} from 'react';
import {Link, Outlet, useNavigate} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {
    AppBar,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {Logout} from '@mui/icons-material';
import UserAvatar from './UserAvatar';
import Footer from './Footer';
import LoginForm from './auth/LoginForm';
import '../styles/components/layout.css';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [lobbyCode, setLobbyCode] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Debug Log für User Data
  React.useEffect(() => {
    if (currentUser) {
      console.log('Layout User Data:', {
        hasRpmAvatar: currentUser.hasRpmAvatar,
        rpmModelUrl: currentUser.rpmModelUrl,
        photoURL: currentUser.photoURL
      });
    }
  }, [currentUser]);

  console.log('Layout: Render', { 
    isLoggedIn: !!currentUser,
    hasRpmAvatar: currentUser?.hasRpmAvatar,
    rpmModelUrl: currentUser?.rpmModelUrl
  });

  // Layout für nicht eingeloggte Benutzer
  if (!currentUser) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="fixed" sx={{ 
          background: 'linear-gradient(180deg, rgb(0 0 0 / 60%) 0%, rgb(0 0 0 / 45%) 100%)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid var(--glass-border)',
          boxShadow: 'var(--glass-shadow)'
        }}>
          <Container className="header-container">
            <Toolbar className="header-toolbar">
              <Box 
                component="div"
                className="logo-container hover-scale"
                onClick={() => navigate('/')}
              >
                <img 
                  src="/logo.png" 
                  alt="Quizzler Logo" 
                  className="logo-image"
                />
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              {!isMobile && <LoginForm compact />}
            </Toolbar>
          </Container>
        </AppBar>
        <Box component="main" sx={{ 
          flexGrow: 1,
          mt: { xs: '64px', sm: '72px' }
        }}>
          <Outlet />
        </Box>
        <Footer />
      </Box>
    );
  }

  const handleJoinLobby = () => {
    if (lobbyCode.trim()) {
      navigate(`/lobby/${lobbyCode.trim().toUpperCase()}`);
      setLobbyCode('');
      setDialogOpen(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleJoinLobby();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Box className={`app-container ${currentUser ? 'logged-in' : ''}`} sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <AppBar position="fixed" sx={{ 
        background: 'linear-gradient(180deg, rgb(0 0 0 / 60%) 0%, rgb(0 0 0 / 45%) 100%)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid var(--glass-border)',
        boxShadow: 'var(--glass-shadow)'
      }}>
        <Container className="header-container">
          <Toolbar className="header-toolbar">
            <Box 
              component="div"
              className="logo-container hover-scale"
              onClick={() => navigate('/')}
            >
              <img 
                src="/logo.png" 
                alt="Quizzler Logo" 
                className="logo-image"
              />
            </Box>

            {/* Mittlerer Bereich - Lobby Input */}
            {!isMobile && (
              <Box className="lobby-input-container">
                <TextField
                  size="small"
                  placeholder="Lobby-Code eingeben"
                  value={lobbyCode}
                  onChange={(e) => setLobbyCode(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  className="lobby-input input-glow"
                />
                <Button
                  variant="contained"
                  onClick={handleJoinLobby}
                  className="join-button button-glow"
                >
                  Beitreten
                </Button>
              </Box>
            )}

            {/* Rechter Bereich - User Information & Actions */}
            <Box className="user-section">
              {/* Mobile Lobby Button */}
              {isMobile && (
                <Button
                  variant="contained"
                  onClick={() => setDialogOpen(true)}
                  size="small"
                  className="join-button button-glow"
                >
                  Lobby beitreten
                </Button>
              )}

              {/* Avatar und Username */}
              {currentUser.isAnonymous ? (
                <Box className="user-profile" onClick={handleLogout} sx={{ cursor: 'pointer' }}>
                  <Box className="avatar-container">
                    <UserAvatar
                      alt={currentUser.displayName}
                      size={32}
                    />
                  </Box>
                  {!isMobile && (
                    <Typography className="user-name">
                      {currentUser.displayName}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Link to={`/profil/${currentUser.profilePath || currentUser.uid}`} className="user-profile">
                  <Box className="avatar-container">
                    <UserAvatar
                      src={currentUser.hasRpmAvatar ? currentUser.rpmModelUrl?.replace('.glb', '.png') : currentUser.photoURL}
                      alt={currentUser.displayName}
                      size={32}
                      hasRpmAvatar={currentUser.hasRpmAvatar}
                      rpmModelUrl={currentUser.rpmModelUrl}
                    />
                  </Box>
                  {!isMobile && (
                    <Typography className="user-name">
                      {currentUser.displayName}
                    </Typography>
                  )}
                </Link>
              )}
              <IconButton
                color="inherit"
                onClick={handleLogout}
                size="small"
                className="logout-button button-glow"
              >
                <Logout />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" className="main-content" sx={{ 
        mt: { xs: '64px', sm: '90px' },
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {children || <Outlet />}
      </Box>
      <Footer />

      {/* Mobile Dialog für Lobby-Beitritt */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Lobby beitreten</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Lobby-Code"
            fullWidth
            variant="outlined"
            value={lobbyCode}
            onChange={(e) => setLobbyCode(e.target.value.toUpperCase())}
            onKeyPress={handleKeyPress}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleJoinLobby} variant="contained">
            Beitreten
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 